import { axiosClient } from './client';

/**
 * Las aplicaciones de la casa, para la rejilla 3×3 de la barra superior.
 *
 * El catálogo lo sirve el backend (`ox-platform/src/core/auth/app-catalog.ts`)
 * y no se replica aquí. Es lo único que puede saber qué tiene contratado este
 * usuario, y una lista duplicada en cinco frontends se separa del original al
 * primer cambio: una aplicación nueva saldría en unas y en otras no.
 */

export type AppState = 'current' | 'granted' | 'locked' | 'coming-soon';

export interface OnxiteApp {
  code: string;
  name: string;
  state: AppState;
  /** Absoluta. `null` cuando falta el grant o aún no hay a dónde ir. */
  url: string | null;
  /** Absoluta. `null` mientras no exista el arte definitivo. */
  iconUrl: string | null;
  brandColor: string;
}

/** Qué aplicación es ésta. Sólo sirve para marcar dónde estás en la rejilla. */
export const APP_CODE = 'xotski';

export async function fetchApps(): Promise<OnxiteApp[]> {
  const { data } = await axiosClient.get<{ apps: OnxiteApp[] }>('/auth/apps', {
    params: { current: APP_CODE },
  });
  // El icono de la PROPIA app es arte del producto y viaja con el bundle: el
  // «midnight whisper» de `public/favicons/favicon.svg`, el mismo de la
  // pestaña del navegador y de xotski.com. Así el selector no depende de lo
  // que haya en `uploads/apps/` de cada entorno.
  return (data.apps ?? []).map((app) =>
    app.code === APP_CODE ? { ...app, iconUrl: '/favicons/favicon.svg' } : app,
  );
}

/**
 * Pide el permiso de paso a otra aplicación.
 *
 * Devuelve un código opaco de un solo uso y 60 segundos de vida, más la URL de
 * destino. El JWT no viaja en la URL a propósito: ahí lo recogerían el
 * historial del navegador, los registros del servidor y la cabecera `Referer`.
 * La aplicación de destino canjea el código por su propio token.
 */
export async function requestAppSwitch(
  code: string,
): Promise<{ code: string; url: string }> {
  const { data } = await axiosClient.post<{ code: string; url: string }>(
    '/auth/app-switch-code',
    { app: code },
  );
  return data;
}

/**
 * Adónde se manda a quien pulsa una aplicación con candado.
 *
 * Un único destino externo para las cinco aplicaciones: el formulario se
 * mantiene en un sitio y comercial sabe dónde mirar. Se le dice qué pedía y
 * desde dónde, porque «alguien quiere presupuesto» sin más no sirve de nada.
 */
export function contactUrl(appCode: string): string {
  const base =
    (import.meta.env.VITE_CONTACT_URL as string | undefined) ??
    'https://onxite.com/contact';
  const url = new URL(base);
  url.searchParams.set('app', appCode);
  url.searchParams.set('from', APP_CODE);
  return url.toString();
}
